// #71 — longest dependency path in the Gantt, so an ETA slip anywhere on it
// is visible at a glance ("critical path" in the classic PM sense).
//
// Graph: nodes are tasks that have at least one of start_date/end_date/deadline
// (see getUnplannedTasks in GanttView.tsx — those are excluded, never given a
// fabricated duration). Edges are task_dependencies, direction depends_on_id
// -> task_id (the depended-on task must finish before the dependent one).
// Weight of an edge = the duration (in days) of the task it points AT, so the
// longest path is found via topological sort + longest-path relaxation
// (Kahn's algorithm, then one relaxation pass in topological order).
//
// task_dependencies only has a DB constraint against self-reference
// (task_dependencies_no_self_check) — a longer cycle (A->B->C->A) is not
// prevented at the schema level, so this function MUST detect it rather than
// loop forever or throw: a cycle means "no well-defined longest path", not
// "pick something anyway".

import type { Task, TaskDependency } from "@juuplan/shared";

export interface CriticalPathResult {
  /** Task ids on the longest path. Empty when there is no path or a cycle was found. */
  taskIds: Set<number>;
  /** Total duration of the critical path in days. 0 when taskIds is empty. */
  totalDays: number;
  /** True when the dependency graph (restricted to dated tasks) contains a cycle. */
  hasCycle: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const EMPTY_RESULT: CriticalPathResult = {
  taskIds: new Set(),
  totalDays: 0,
  hasCycle: false,
};

// Mirrors buildItems' own start/end resolution in GanttView.tsx, so a task's
// critical-path weight matches the bar length actually drawn for it.
function taskDurationDays(t: Task): number | null {
  const startStr = t.start_date ?? t.end_date ?? t.deadline;
  if (!startStr) return null; // unplanned — never given a fabricated duration
  const start = new Date(startStr);
  if (Number.isNaN(start.getTime())) return null;

  const endStr = t.end_date ?? t.start_date ?? t.deadline;
  const end = endStr ? new Date(endStr) : start;
  if (Number.isNaN(end.getTime())) return null;

  const days = Math.round((end.getTime() - start.getTime()) / DAY_MS);
  return Math.max(1, days); // same-day tasks still count as 1 day, like the bar
}

export function computeCriticalPath(
  tasks: Task[],
  dependencies: TaskDependency[]
): CriticalPathResult {
  if (tasks.length === 0) return EMPTY_RESULT;

  const duration = new Map<number, number>();
  for (const t of tasks) {
    const d = taskDurationDays(t);
    if (d !== null) duration.set(t.id, d);
  }
  if (duration.size === 0) return EMPTY_RESULT;

  // Adjacency depends_on_id -> [task_id, ...]. Edges touching an undated task
  // are dropped — that task can't sit on a path with a real length anyway.
  const adj = new Map<number, number[]>();
  const indegree = new Map<number, number>();
  for (const id of duration.keys()) indegree.set(id, 0);
  for (const dep of dependencies) {
    if (!duration.has(dep.task_id) || !duration.has(dep.depends_on_id)) continue;
    const list = adj.get(dep.depends_on_id) ?? [];
    list.push(dep.task_id);
    adj.set(dep.depends_on_id, list);
    indegree.set(dep.task_id, (indegree.get(dep.task_id) ?? 0) + 1);
  }

  // Kahn's algorithm. If it can't consume every dated task, a cycle exists
  // among them.
  const work = new Map(indegree);
  const queue: number[] = [];
  for (const [id, deg] of indegree) if (deg === 0) queue.push(id);
  const order: number[] = [];
  while (queue.length > 0) {
    const id = queue.shift() as number;
    order.push(id);
    for (const next of adj.get(id) ?? []) {
      const deg = (work.get(next) ?? 0) - 1;
      work.set(next, deg);
      if (deg === 0) queue.push(next);
    }
  }
  if (order.length !== duration.size) {
    return { taskIds: new Set(), totalDays: 0, hasCycle: true };
  }

  // Longest-path relaxation over the topological order: dist[id] = length of
  // the longest path ending at (and including) id.
  const dist = new Map<number, number>();
  const prev = new Map<number, number | null>();
  for (const id of order) {
    dist.set(id, duration.get(id) ?? 0);
    prev.set(id, null);
  }
  for (const id of order) {
    const base = dist.get(id) ?? 0;
    for (const next of adj.get(id) ?? []) {
      const candidate = base + (duration.get(next) ?? 0);
      if (candidate > (dist.get(next) ?? 0)) {
        dist.set(next, candidate);
        prev.set(next, id);
      }
    }
  }

  let endId: number | null = null;
  let best = -Infinity;
  for (const [id, d] of dist) {
    if (d > best) {
      best = d;
      endId = id;
    }
  }
  if (endId === null) return EMPTY_RESULT;

  const path: number[] = [];
  for (let cur: number | null = endId; cur !== null; cur = prev.get(cur) ?? null) {
    path.push(cur);
  }

  return { taskIds: new Set(path), totalDays: best, hasCycle: false };
}
